import Icon from '@components/v2/ui/Icon/Icon';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import { Box, IconButton, InputBase } from '@mui/material';
import { ArrowUp } from 'lucide-react';
import { KeyboardEvent, useRef } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import { isRachelImageMediaType } from '../../rachel.files.presentation';
import { RachelAttachment } from '../../rachel.files.types';
import RachelSparkIcon from '../RachelSparkIcon/RachelSparkIcon';
import RachelAttachmentCard from './components/RachelAttachmentCard/RachelAttachmentCard';
import RachelAttachmentThumbnail from './components/RachelAttachmentThumbnail/RachelAttachmentThumbnail';
import RachelContextChip from './components/RachelContextChip/RachelContextChip';
import RachelInputAttachButton from './components/RachelInputAttachButton/RachelInputAttachButton';
import { type RachelInputExpandDirection, useRachelInputStyles } from './rachelInput.styles';
import { useRachelAttachmentsAutoScroll } from './useRachelAttachmentsAutoScroll';

const DEFAULT_MIN_HEIGHT = 28;

interface RachelInputViewProps {
  value: string;
  minHeight?: number;
  expandDirection?: RachelInputExpandDirection;
  isRound?: boolean;
  isActive: boolean;
  isVertical: boolean;
  isStreaming: boolean;
  hasText: boolean;
  sparkLabel?: LangEnum;
  attachments?: RachelAttachment[];
  hasBlockingAttachment?: boolean;
  contextChip?: { label: string; onDismiss: () => void } | null;
  shouldReserveContextChipSpace?: boolean;
  inputRef: (node: HTMLInputElement | null) => void;
  onChange: (value: string) => void;
  onFocus: () => void;
  onBlur: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onSend: () => void;
  onStop: () => void;
  onSparkClick?: () => void;
  onAttachFiles?: (files: File[]) => void;
  onRemoveAttachment?: (id: string) => void;
  onHoverChange: (isHovered: boolean) => void;
}

const RachelInputView = ({
  value,
  minHeight = DEFAULT_MIN_HEIGHT,
  expandDirection = 'up',
  isRound = false,
  isActive,
  isVertical,
  isStreaming,
  hasText,
  sparkLabel = LangEnum.RACHEL_OPEN,
  attachments = [],
  hasBlockingAttachment = false,
  contextChip,
  shouldReserveContextChipSpace = false,
  inputRef,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onSend,
  onStop,
  onSparkClick,
  onAttachFiles,
  onRemoveAttachment,
  onHoverChange,
}: RachelInputViewProps) => {
  const { translate } = useTranslation();
  const attachmentsRowRef = useRef<HTMLDivElement>(null);

  useRachelAttachmentsAutoScroll(attachmentsRowRef, attachments.length);

  const hasReadyAttachment = attachments.some((attachment) => attachment.status === 'ready');
  const hasSendableContent = hasText || hasReadyAttachment;
  const canSend = hasSendableContent && !hasBlockingAttachment;
  const hasAttachments = attachments.length > 0;

  const { classes } = useRachelInputStyles({
    minHeight,
    isContextChipSpaceReserved: shouldReserveContextChipSpace && !!contextChip,
    expandDirection,
    isActive,
    isTyping: hasText,
    isVertical,
    isMerged: hasAttachments || !!contextChip,
    isRound,
    isSparkInteractive: !!onSparkClick,
  });

  const renderActionButton = () => {
    if (isStreaming) {
      return (
        <IconButton
          size="small"
          className={classes.actionButton}
          aria-label={translate(LangEnum.RACHEL_STREAM_INTERRUPTED)}
          onClick={onStop}
        >
          <Box className={classes.stopSquare} />
        </IconButton>
      );
    }

    if (!canSend) {
      return null;
    }

    if (hasSendableContent && hasBlockingAttachment) {
      return (
        <IconButton
          disabled
          size="small"
          className={classes.actionButton}
          aria-label={translate(LangEnum.RACHEL_INPUT_SEND)}
        >
          <Icon icon={ArrowUp} size="md" />
        </IconButton>
      );
    }

    return (
      <IconButton
        size="small"
        className={classes.actionButton}
        aria-label={translate(LangEnum.RACHEL_INPUT_SEND)}
        onClick={onSend}
      >
        <Icon icon={ArrowUp} size="md" />
      </IconButton>
    );
  };

  const attachmentsRow = hasAttachments ? (
    <Box ref={attachmentsRowRef} className={classes.attachmentsRow}>
      {attachments.map((attachment) =>
        isRachelImageMediaType(attachment.file.type) ? (
          <RachelAttachmentThumbnail
            key={attachment.id}
            attachment={attachment}
            onRemove={(id) => onRemoveAttachment?.(id)}
          />
        ) : (
          <RachelAttachmentCard
            key={attachment.id}
            attachment={attachment}
            onRemove={(id) => onRemoveAttachment?.(id)}
          />
        ),
      )}
    </Box>
  ) : null;

  const sparkIcon = <RachelSparkIcon size={16} color="currentColor" />;
  const sparkLabelText = translate(sparkLabel);

  const spark = onSparkClick ? (
    <Box className={classes.sparkSlot}>
      <Tooltip TooltipContent={sparkLabelText} tabIndex={-1} placement="top">
        <button
          type="button"
          className={classes.sparkStroke}
          aria-label={sparkLabelText}
          onClick={onSparkClick}
        >
          {sparkIcon}
        </button>
      </Tooltip>
    </Box>
  ) : (
    <Box component="span" aria-hidden className={classes.sparkSlot}>
      <Box component="span" className={classes.sparkStroke}>
        {sparkIcon}
      </Box>
    </Box>
  );

  const pill = (
    <Box className={classes.pill}>
      {spark}
      <InputBase
        fullWidth
        multiline
        maxRows={4}
        inputProps={{ rows: 1 }}
        value={value}
        className={classes.input}
        inputRef={inputRef}
        placeholder={translate(LangEnum.RACHEL_INPUT_PLACEHOLDER)}
        onChange={(event) => onChange(event.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      />
      <Box className={classes.actionsGroup}>
        {onAttachFiles && (
          <RachelInputAttachButton
            isVisible={isActive}
            className={classes.iconButton}
            onAttachFiles={onAttachFiles}
          />
        )}
        {renderActionButton()}
      </Box>
    </Box>
  );

  return (
    <Box
      className={classes.anchor}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      <Box className={classes.stack}>
        <Box className={classes.glow} />
        <Box className={classes.card}>
          {attachmentsRow}
          {contextChip && (
            <RachelContextChip label={contextChip.label} onDismiss={contextChip.onDismiss} />
          )}
          {pill}
        </Box>
        <Box className={classes.outline} />
      </Box>
    </Box>
  );
};

export default RachelInputView;
